import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { PaginatedTable } from "../../components/Table/PaginatedTable";
import { Empresa } from "../../types/empresa";
import { empresaService } from "../../services/empresaService";

export const ListaAssinantes: React.FC = () => {
  const [assinantes, setAssinantes] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchAssinantes = async () => {
    try {
      setLoading(true);
      // O backend espera 'search', então usamos o termo de busca aqui
      const response = await empresaService.listar(
        pageSize,
        (currentPage - 1) * pageSize,
        searchTerm
      );
      setAssinantes(response.results);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Erro ao buscar assinantes:", error);
      toast.error("Erro ao carregar a lista de assinantes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchAssinantes();
    }, 500); // Debounce para evitar muitas chamadas à API

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, pageSize]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Resetar para a primeira página ao filtrar
  };

  const limparFiltros = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleExcluir = async (id: number) => {
    if (window.confirm("Tem certeza que deseja excluir este assinante?")) {
      try {
        await empresaService.excluir(id);
        fetchAssinantes(); // Recarregar a lista
        toast.success("Assinante excluído com sucesso.");
      } catch (error) {
        console.error("Erro ao excluir assinante:", error);
        toast.error("Erro ao excluir assinante.");
      }
    }
  };

  const columns = [
    {
      key: "nome" as keyof Empresa,
      title: "Nome / Razão Social",
      render: (_: any, record: Empresa) => (
        <span className="font-medium text-gray-900">
          {record.tipo === "PJ"
            ? record.razao_social
            : record.usuario_pf?.nome_completo}
        </span>
      ),
    },
    {
      key: "tipo" as keyof Empresa,
      title: "Tipo",
    },
    {
      key: "email" as keyof Empresa,
      title: "Email do Admin",
      render: (_: any, record: Empresa) => (
        <span>
          {record.membros?.find((m) => m.cargo === "ADMIN")?.user.email ||
            "Não informado"}
        </span>
      ),
    },
    {
      key: "status" as keyof Empresa,
      title: "Status",
      render: (_: any, record: Empresa) => (
        <span
          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
            record.admin_status?.status === "Ativo"
              ? "bg-green-100 text-green-800"
              : record.admin_status?.status === "Pendente"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {record.admin_status?.status || "Pendente"}
        </span>
      ),
    },
    {
      key: "acoes" as keyof Empresa,
      title: "Ações",
      render: (_: any, record: Empresa) => (
        <div className="flex space-x-2">
          <Link
            to={`/assinantes/${record.id}/detalhes`}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
          >
            Detalhes
          </Link>
          <button
            onClick={() => handleExcluir(record.id)}
            className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
          >
            Excluir
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Assinantes</h1>
        <Link
          to="/assinantes/novo"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Novo Assinante
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-lg font-semibold mb-4">Filtros</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar por Nome, Email, etc.
            </label>
            <input
              type="text"
              placeholder="Digite sua busca..."
              value={searchTerm}
              onChange={handleFilterChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <button
            onClick={limparFiltros}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Limpar Filtros
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <PaginatedTable
          data={assinantes}
          columns={columns}
          loading={loading}
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / pageSize)}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};
